import {
    CommandGroup,
    CommandItem,
    CommandList,
} from "./command"
import { Command as CommandPrimitive } from "cmdk"
import { useState, useRef, useCallback, type KeyboardEvent, useEffect } from "react"

import { Skeleton } from "./skeleton"

import { Check } from "lucide-react"
import { cn } from "@/lib/utils";

export type AutoCompleteOption = {
    label: string
    value: string
    component?: React.ReactNode
}

type AutoCompleteProps = {
    options: AutoCompleteOption[]
    emptyMessage?: string
    value?: string
    onValueChange?: (value: string) => void
    isLoading?: boolean
    disabled?: boolean
    className?: string
    placeholder?: string
}

export const AutoComplete = ({
    options,
    placeholder,
    emptyMessage,
    value,
    onValueChange,
    className,
    disabled,
    isLoading = false,
}: AutoCompleteProps) => {
    const inputRef = useRef<HTMLInputElement>(null)

    const [isOpen, setOpen] = useState(false);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            const input = inputRef.current
            if (!input) {
                return
            }

            // Keep the options displayed when the user is typing
            if (!isOpen) {
                setOpen(true)
            }

            // This is not a default behaviour of the <input /> field
            if (e.key === "Enter") {
                const optionToSelect = options.find(
                    (option) => option.label === input.value,
                )
                console.log(input.value);

                if (optionToSelect) {
                    onValueChange?.(optionToSelect.value)
                }
            }

            if (e.key === "Escape") {
                input.blur()
            }
        },
        [isOpen, options, onValueChange],
    )

    const handleBlur = useCallback(() => {
        const input = inputRef.current
        if (!input) {
            return
        }

        setOpen(false)
    }, [])

    const handleSelectOption = useCallback(
        (selectedOption: AutoCompleteOption) => {
            console.log(selectedOption.value);
            onValueChange?.(selectedOption.value || "")

            // This is a hack to prevent the input from being focused after the user selects an option
            // We can call this hack: "The next tick"
            setTimeout(() => {
                inputRef?.current?.blur()
            }, 0)
        },
        [onValueChange],
    );

    return (
        <CommandPrimitive onKeyDown={handleKeyDown} className={className}>
            <div>
                <div className={cn("flex items-center")} cmdk-input-wrapper="">
                    <CommandPrimitive.Input
                        ref={inputRef}
                        value={value}
                        className={cn(
                            "flex h-10 w-full rounded-md border border-[#2E3835] bg-[#111] px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                            "text-base"
                        )}
                        disabled={disabled}
                        onValueChange={onValueChange}
                        onBlur={handleBlur}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                    />
                </div>
            </div>
            <div className="relative mt-1">
                <div
                    className={cn(
                        "animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded  bg-black",
                        isOpen ? "block" : "hidden",
                    )}
                >
                    <CommandList >
                        {isLoading ? (
                            <CommandPrimitive.Loading>
                                <div className="p-1">
                                    <Skeleton className="h-8 w-full" />
                                </div>
                            </CommandPrimitive.Loading>
                        ) : null}
                        {options.length > 0 && !isLoading ? (
                            <CommandGroup>
                                {options.map((option) => {
                                    const isSelected = value === option.value
                                    return (
                                        <CommandItem
                                            key={option.value}
                                            value={option.label}
                                            onMouseDown={(event) => {
                                                event.preventDefault()
                                                event.stopPropagation()
                                            }}
                                            onSelect={() => handleSelectOption(option)}
                                            className={cn(
                                                "flex w-full items-center gap-2",
                                                !isSelected ? "pl-8" : null,
                                            )}
                                        >
                                            {isSelected ? <Check className="w-4" /> : null}
                                            {option.component || option.label}
                                        </CommandItem>
                                    )
                                })}
                            </CommandGroup>
                        ) : null}
                        {!isLoading && emptyMessage ? (
                            <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                                {emptyMessage}
                            </CommandPrimitive.Empty>
                        ) : null}
                    </CommandList>
                </div>
            </div>
        </CommandPrimitive>
    )
}